import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useVehicles } from '../hooks/useVehicles';
import { Modal } from '../components/common/Modal';
import { FileUpload } from '../components/common/FileUpload';
import { ScanDocument } from '../components/common/ScanDocument';
import { ArchivedFiles } from '../components/common/ArchivedFiles';
import { archiveReplacedFile } from '../lib/fileArchive';
import { FileText, Plus, ExternalLink, Trash2, Search, Edit2, Camera } from 'lucide-react';
import { useOwner } from '../hooks/useOwner';
import toast from 'react-hot-toast';

const DOC_TYPES = ['Title Deed', 'Insurance', 'Road Tax', 'MOT/KTEO', 'Registration', 'Mechanical Report', 'Invoice', 'Other'];
// Title deeds live in their own title_deeds table (managed from each vehicle's Title Deed
// tab) rather than the general documents table, so they're excluded from the "new document"
// type picker here — but they're still merged into this list below and stay filterable.
const CREATE_DOC_TYPES = DOC_TYPES.filter(t => t !== 'Title Deed');

export const Documents = () => {
  const { vehicles } = useVehicles();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const { role, isOwner, isAdmin } = useOwner();
  const canEdit   = isOwner || isAdmin || role === 'editor';
  const canDelete = isOwner || isAdmin;

  // The phone app's centre "Scan document" button opens this page with ?scan=1.
  const quickOpened = useRef(false);
  useEffect(() => {
    if (quickOpened.current || !canEdit) return;
    if (new URLSearchParams(window.location.search).get('scan') === '1') {
      quickOpened.current = true;
      setShowScan(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [canEdit]);
  const [search, setSearch] = useState('');
  // /documents?vehicle=<id> (from a car's page on the phone) opens already filtered to that car.
  const [filterVehicle, setFilterVehicle] = useState(() => new URLSearchParams(window.location.search).get('vehicle') || '');
  const [filterType, setFilterType] = useState('');
  const navigate = useNavigate();

  const fetch = async () => {
    setLoading(true);
    const [{ data: docsData }, { data: tdData }] = await Promise.all([
      supabase
        .from('documents')
        .select('*, vehicles(id,registration_number,brand,model)')
        .order('created_at', { ascending: false }),
      supabase
        .from('title_deeds')
        .select('*, vehicles(id,registration_number,brand,model)')
        .order('created_at', { ascending: false }),
    ]);
    const normalizedDocs = (docsData || []).map(d => ({ ...d, _source: 'documents' }));
    // title_deeds rows don't have their own name/doc_type — synthesize both so they
    // display and filter just like a regular document.
    const normalizedTd = (tdData || []).map(t => ({
      ...t,
      _source: 'title_deeds',
      doc_type: 'Title Deed',
      name: t.owner_name ? `Title Deed — ${t.owner_name}` : 'Title Deed',
    }));
    const merged = [...normalizedDocs, ...normalizedTd].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    setDocs(merged);
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const deleteDoc = async (doc) => {
    if (!window.confirm('Delete this document?')) return;
    if (doc._source === 'title_deeds') {
      await supabase.from('title_deeds').delete().eq('id', doc.id);
    } else {
      await supabase.from('documents').delete().eq('id', doc.id);
    }
    toast.success('Document deleted');
    fetch();
  };

  const filtered = docs.filter(d => {
    const q = search.toLowerCase();
    const matchesSearch = !q
      || d.vehicles?.registration_number?.toLowerCase().includes(q)
      || d.vehicles?.brand?.toLowerCase().includes(q)
      || d.vehicles?.model?.toLowerCase().includes(q)
      || d.name?.toLowerCase().includes(q)
      || d.doc_type?.toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (filterVehicle && d.vehicle_id !== filterVehicle) return false;
    if (filterType && d.doc_type !== filterType) return false;
    return true;
  });

  const activeFilters = [filterVehicle, filterType].filter(Boolean).length + (search ? 1 : 0);
  const clearFilters = () => { setSearch(''); setFilterVehicle(''); setFilterType(''); };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Documents</div>
          <div className="page-subtitle">{docs.length} documents stored</div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" onClick={() => setShowScan(true)}>
              <Camera size={14} /> Scan Document
            </button>
            <button className="btn btn-primary" onClick={() => { setEditRecord(null); setShowModal(true); }}>
              <Plus size={14} /> Upload Document
            </button>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
          <input
            className="form-input"
            style={{ paddingLeft: 32 }}
            placeholder="Search by name, type, registration, make or model…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
          <select className="form-input" value={filterVehicle} onChange={e => setFilterVehicle(e.target.value)}>
            <option value="">All vehicles</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>{v.registration_number} — {v.brand} {v.model}</option>
            ))}
          </select>
          <select className="form-input" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All types</option>
            {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {activeFilters > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, fontSize: 12, color: 'var(--text3)' }}>
            <span>Showing {filtered.length} of {docs.length} documents</span>
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear filters</button>
          </div>
        )}
      </div>
      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty-state">
            <FileText />
            <h3>{activeFilters > 0 ? 'No matching documents' : 'No documents found'}</h3>
            <p>{activeFilters > 0 ? 'Try adjusting or clearing your filters.' : 'Upload documents to keep everything organised.'}</p>
            {activeFilters > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={clearFilters}>Clear filters</button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Document Name</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.id}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: 1, textTransform: 'uppercase' }}>
                          {d.vehicles?.registration_number}
                        </span>
                        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 500, color: 'var(--text2)' }}>
                          {d.vehicles?.brand} {d.vehicles?.model}
                        </span>
                      </div>
                    </td>
                    <td className="td-primary">{d.name}</td>
                    <td><span className="badge badge-blue">{d.doc_type || '—'}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {d.document_url && (
                          <a href={d.document_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                            <ExternalLink size={12} />
                          </a>
                        )}
                        {canEdit && (
                          <button className="btn btn-ghost btn-sm" onClick={() => {
                            setEditRecord(d);
                            setShowModal(true);
                          }}>
                            <Edit2 size={12} />
                          </button>
                        )}
                        {canDelete && (
                          <button className="btn btn-danger btn-sm" onClick={() => deleteDoc(d)}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showScan && (
        <ScanDocument
          vehicles={vehicles}
          onClose={() => setShowScan(false)}
          onSaved={fetch}
        />
      )}

      {showModal && (
        editRecord?._source === 'title_deeds' ? (
          <TitleDeedEditModal
            record={editRecord}
            onClose={() => {
              setShowModal(false);
              setEditRecord(null);
              fetch();
            }}
          />
        ) : (
          <DocModal
            vehicles={vehicles}
            record={editRecord}
            onClose={() => {
              setShowModal(false);
              setEditRecord(null);
              fetch();
            }}
          />
        )
      )}
    </div>
  );
};

const DocModal = ({ vehicles, onClose, record = null }) => {
  const [form, setForm] = useState({
    vehicle_id: record?.vehicle_id || '',
    name: record?.name || '',
    doc_type: record?.doc_type || CREATE_DOC_TYPES[0],
    document_url: record?.document_url || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error('Please select a vehicle'); return; }
    if (!form.name) { toast.error('Please enter a document name'); return; }
    setSaving(true);

    const payload = {
      vehicle_id: form.vehicle_id,
      name: form.name,
      doc_type: form.doc_type || null,
      document_url: form.document_url || null,
      notes: form.notes || null,
    };

    let error;
    if (record) {
      ({ error } = await supabase.from('documents').update(payload).eq('id', record.id));
    } else {
      ({ error } = await supabase.from('documents').insert(payload).select());
    }

    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    // Keep the document that was just replaced under "Previous versions".
    if (record) await archiveReplacedFile({ vehicleId: record.vehicle_id, slot: `documents:${record.id}`, oldUrl: record.document_url, newUrl: payload.document_url, label: record.name });
    toast.success(record ? 'Document updated!' : 'Document uploaded!');
    onClose();
    setSaving(false);
  };

  return (
    <Modal
      title={record ? 'Edit Document' : 'Upload Document'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="doc-form" type="submit" disabled={saving}>
            {saving ? 'Saving…' : record ? 'Update' : 'Save'}
          </button>
        </>
      }
    >
      <form id="doc-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-group">
          <label className="form-label">Vehicle *</label>
          <select className="form-input" required value={form.vehicle_id} onChange={e => set('vehicle_id', e.target.value)}>
            <option value="">Select vehicle…</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>{v.registration_number} — {v.brand} {v.model}</option>
            ))}
          </select>
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Document Name *</label>
            <input className="form-input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Insurance Policy 2025" />
          </div>
          <div className="form-group">
            <label className="form-label">Document Type</label>
            <select className="form-input" value={form.doc_type} onChange={e => set('doc_type', e.target.value)}>
              {CREATE_DOC_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <FileUpload
          folder="documents"
          onUploaded={(r) => {
            console.log('File uploaded:', r.url);
            set('document_url', r.url);
          }}
          label={record?.document_url ? 'Upload new version — the old one is kept' : 'Drop document here or click to upload'}
        />
        {record && (
          <ArchivedFiles
            vehicleId={record.vehicle_id}
            slot={`documents:${record.id}`}
            current={{ url: record.document_url, path: null }}
            target={{ table: 'documents', rowId: record.id, urlField: 'document_url' }}
            onChanged={onClose}
          />
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};

// Edits an existing title_deeds row from the Documents list. Title deeds are created from
// the vehicle's own Title Deed tab (they need a vehicle picked first), so this modal only
// handles editing one that already exists — the vehicle itself isn't changeable here.
const TitleDeedEditModal = ({ record, onClose }) => {
  const [form, setForm] = useState({
    owner_name: record?.owner_name || '',
    issue_date: record?.issue_date || '',
    document_url: record?.document_url || '',
    document_path: record?.file_path || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      owner_name: form.owner_name || null,
      issue_date: form.issue_date || null,
      document_url: form.document_url || null,
      file_path: form.document_path || null,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase.from('title_deeds').update(payload).eq('id', record.id);
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    await archiveReplacedFile({ vehicleId: record.vehicle_id, slot: `title_deed:${record.id}`, oldUrl: record.document_url, oldPath: record.file_path, newUrl: payload.document_url, label: 'Title deed' });
    toast.success('Title deed updated!');
    onClose();
    setSaving(false);
  };

  return (
    <Modal
      title="Edit Title Deed"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="td-edit-form" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Update'}
          </button>
        </>
      }
    >
      <form id="td-edit-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text2)' }}>
          {record?.vehicles?.registration_number} — {record?.vehicles?.brand} {record?.vehicles?.model}
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Registered Owner</label>
            <input className="form-input" value={form.owner_name} onChange={e => set('owner_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Issue Date</label>
            <input className="form-input" type="date" value={form.issue_date || ''} onChange={e => set('issue_date', e.target.value)} />
          </div>
        </div>
        {form.document_url && (
          <a href={form.document_url} target="_blank" rel="noreferrer" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--primary)' }}>
            <ExternalLink size={12} /> View current document
          </a>
        )}
        <FileUpload
          folder="title-deed"
          label={form.document_url ? 'Upload new version (PDF/JPG/PNG) — the old one is kept' : 'Upload document (PDF/JPG/PNG)'}
          onUploaded={(r) => { set('document_url', r.url); set('document_path', r.path); }}
        />
        <ArchivedFiles
          vehicleId={record.vehicle_id}
          slot={`title_deed:${record.id}`}
          current={{ url: record.document_url, path: record.file_path }}
          target={{ table: 'title_deeds', rowId: record.id, urlField: 'document_url', pathField: 'file_path' }}
          onChanged={onClose}
        />
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};